import React, { useState } from "react"
import classNames from "classnames"
import ArrowRightIcon from "@dustin-web/microsite-components/src/icon/generated/arrow-right"
import { useSiteContext, useTranslations } from "@dustin-web/microsite-components/src/hooks"
import { getCultureCode, getDefaultLanguage } from "@dustin-web/microsite-config"
import { CarouselTest } from "@dustin-web/microsite-components/src/carousel-test/carousel"
import type { LandingPageGroupList } from "../../../data/types"
import { getCtaLink } from "../../../utils/attributes"
import { getBgColorThemeClass, getColorThemeClass } from "../../../utils/color-theme"
import { ListItemRenderer } from "../../../../product-list"
import { getDigitalDataOrigin } from "../../../utils/digital-data"

type Props = {
  groupLists: LandingPageGroupList[]
}

const LandingPageVariantMultiSlider = ({ groupLists }: Props) => {
  const { translate } = useTranslations()
  const { site } = useSiteContext()
  const lang = getDefaultLanguage(site)
  const langCultureCode = getCultureCode(lang)
  const [activeTab, setActiveTab] = useState(0)

  const tabNames = groupLists.map((list) => list.attributes.displayName)

  const [list] = groupLists ?? []
  const { attributes } = list ?? {}
  const { colorTheme = 'gray', groupDisplayName, ctaText } = attributes ?? {}

  const ctaLink = getCtaLink(attributes, langCultureCode)

  return (
    <>
      <div
        className={`c-tabs u-w-100 ${getBgColorThemeClass(colorTheme, 'light')}`}>
        {groupDisplayName && (
          <h2
            className={`h2 ${getColorThemeClass(colorTheme)}`}>
            {translate(groupDisplayName)}
          </h2>
        )}
        <div
          className={classNames('d-flex flex-row c-tabs--tablist u-mb-16', {
            'u-bb border-dark-purple': colorTheme === 'purple',
            'u-bb border-black': colorTheme === 'gray',
            'u-bb border-dark-green': colorTheme === 'green',
            'u-bb border-dark-blue': colorTheme === 'blue',
          })}>
          {tabNames?.map(
            (tabName, i) =>
              tabName && (
                <button
                  key={i}
                  onClick={() => setActiveTab(i)}
                  className={classNames(
                    `button-stripped u-decoration-none u-mb-0 u-pb-8 u-mr-32 c-tabs--tablist--single ${getColorThemeClass(colorTheme)}`,
                    {
                      'u-fw-bold u-bb c-tabs-active--tab': activeTab === i,
                    }
                  )}>
                  {translate(tabName)}
                </button>
              )
          )}
        </div>
        <div className="tabs-content">
          {!!groupLists[activeTab] && (
            <CarouselTest
              partialVisible
              dots
              dotMode="full-width"
              type="startpage"
              colorTheme={colorTheme}
              list={groupLists[activeTab].productReferences?.map(
                ({ product, ticket }, key) =>
                  product && (
                    <ListItemRenderer
                      key={product.id}
                      imagePriority
                      product={product}
                      ticket={ticket}
                      listType={'stripped'}
                      gallerySizeClasses={'slider-card u-px-8 u-mb-sm-0'}
                      digitalDataOrigin={getDigitalDataOrigin(groupLists[activeTab].attributes.displayName!)}
                      digitalIndex={key}
                      hTag="h3"
                    />
                  )
              )}
              activeTab={activeTab}
            />
          )}
        </div>
      </div>
      {ctaLink && ctaText && (
        <a
          className={`
            d-flex
            align-items-center
            u-button-opacity
            u-px-16
            u-radius-2
            u-color-white
            u-decoration-none
            u-fs-14
            u-h-40
            ${getBgColorThemeClass(colorTheme, 'dark')}
          `}
          href={ctaLink}
          style={{ marginLeft: 'auto', width: 'fit-content' }}>
          <span className="u-mr-32">{translate(ctaText)}</span>
          <ArrowRightIcon className={'i-16'} />
        </a>
      )}
    </>
  )
}

export default LandingPageVariantMultiSlider
